import type { ReactNode } from "react";

import { classes } from "./classes.js";
import {
  BANK_BG_SURFACE,
  BANK_BORDER,
  BANK_FIGURE,
  BANK_FS_BODY,
  BANK_RADIUS_LG,
  BANK_TEXT,
  BANK_TEXT_2,
} from "./tokens.js";

/**
 * `F-0b`: the bar that holds a screen's decision (prototype `.actionbar`): sticky at the foot of its container (D2 `components.css:181`: 14 px above it, lifted by `--hk-e3`),
 * wrapping, with a counter, a hint and the actions.
 *
 * It wraps instead of clipping, which is the defect of the three hand-made rows it replaces (DX-B-001/-002).
 * Its children are the actions (a `GuardedButton` for each unavailable one, so the reason is visible); the bar
 * itself knows no business and no French.
 */
export interface ActionBarProps {
  /** The region's accessible name, from the catalogue. */
  readonly label: string;
  readonly children: ReactNode;
  /** « 3 sur 5 »: a count, drawn in the figure face. */
  readonly count?: ReactNode;
  /** One sentence under the actions, on its own line. */
  readonly hint?: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

export function ActionBar({
  label,
  children,
  count,
  hint,
  className,
  testId,
}: ActionBarProps): React.JSX.Element {
  return (
    <div
      role="region"
      aria-label={label}
      data-kit="actionbar"
      data-testid={testId}
      className={classes(
        "sticky bottom-3.5 z-20 flex flex-wrap items-center gap-x-3 gap-y-2 border px-5 py-3",
        BANK_BG_SURFACE,
        BANK_BORDER,
        BANK_RADIUS_LG,
        "[box-shadow:var(--hk-e3)]",
        className,
      )}
    >
      {count === undefined ? null : (
        <span className={classes("font-semibold", BANK_TEXT, BANK_FIGURE, "text-[length:16px]")}>
          {count}
        </span>
      )}
      <div className="flex flex-1 flex-wrap items-center gap-3">{children}</div>
      {hint === undefined ? null : (
        <p className={classes("m-0 basis-full", BANK_FS_BODY, BANK_TEXT_2)}>{hint}</p>
      )}
    </div>
  );
}
